/**
 * @file useIsMobile.js
 * @description Custom React hook that returns true when the viewport width is below
 *   the given breakpoint. Uses a debounced resize listener to minimize re-renders.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module hooks/useIsMobile
 */
import { useState, useEffect } from "react";

/**
 * @description Returns true when the current viewport width is below the breakpoint.
 * @param {number} [breakpoint=768] - Width threshold in pixels
 * @returns {boolean} Whether the current viewport qualifies as mobile
 */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    let timer;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth < breakpoint), 80);
    };
    window.addEventListener("resize", handler);
    return () => { window.removeEventListener("resize", handler); clearTimeout(timer); };
  }, [breakpoint]);
  return isMobile;
}
